import { Injectable } from '@angular/core';
import { BatchMoveService } from './batch-move.service';
import { IBatchAction, IFile, INameData } from 'src/sync';
import { ConflictData, GET_DUPLICATE_NAME_CHECK_RES, IDuplicateNameMap, IFileConflict, IShareKeyData } from '../models/move.model';
import { DirtyOutService } from 'src/app/core/crypt/dirty-out.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { BlendEvent, DirtyOutKeyItemShareKey, WebpathActionType } from '../models';
import { FileListService } from 'src/app/file-list/services/file-list.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { BatchCopyService } from './batch-copy.service';
import { BlendService } from './blend.service';
import { AtomicMoveService } from './atomic-move.service';
import { FeatureService } from './feature.service';
import { UserService } from 'src/app/core/user.service';

@Injectable({
  providedIn: 'root'
})
export class MoveActionsService {
  public isLinkPreserveAllowed: boolean;

  constructor(
    private blendService: BlendService,
    private batchMoveService: BatchMoveService,
    private DirtyOut: DirtyOutService,
    protected SyncCrypt: SyncCryptService,
    private fileListService: FileListService,
    private notificationsService: NotificationsService,
    private batchCopyService: BatchCopyService,
    public atomicMoveService: AtomicMoveService,
    public featureService: FeatureService,
    public userService: UserService,
  ) { 
    // Temporary addition to handle feature rollout.
    // This will be removed once the feature is released to all users.
    this.setLinkPreserveFlag();
  }

  async setLinkPreserveFlag() {
    this.isLinkPreserveAllowed = await this.featureService.isAllowed('linkPreserve') || this.userService.get('has_beta_access') == 1;
  }

  getDuplicateNameCheck = async (sync_id: number, itemList: IFile[]): Promise<GET_DUPLICATE_NAME_CHECK_RES> => {
    const destination_id: string = `${sync_id}`
    const sync_ids: string[] = [];
    const conflictData: ConflictData = {};

    for (let i = itemList.length - 1; i >= 0; i--) {
      sync_ids.push(`${itemList[i].sync_id}`)
    }

    let conflictDataRes = {};
    if (itemList[0].pid == sync_id) {
      // Don't call API if selected items are already in destination folder. In this case all selected items are conflicted
      for (let i = 0; i < sync_ids.length; i++) {
        conflictDataRes[sync_ids[i]] = true;
      }
    } else {
      const res = await this.batchMoveService.getDuplicateNameCheck(sync_ids, destination_id);
      conflictDataRes = res['result'];
    }
    const conflictQueue: IFileConflict[] = [];
    const len = itemList.length;
    let conflictFileName = '';
    let count = 0;
    let _sync_id: number = 0;

    for (const sync_id in conflictDataRes) {
      if (Object.prototype.hasOwnProperty.call(conflictDataRes, sync_id)) {
        const val = conflictDataRes[sync_id];
        if (val) {
          _sync_id = +sync_id;
          count++;
        }
      }
    }

    for (let i = 0; i < len; i++) {
      if (itemList[i].sync_id == _sync_id) {
        conflictFileName = itemList[i].name;
      }
      const item = { ...itemList[i] }
      if (Object.prototype.hasOwnProperty.call(conflictDataRes, itemList[i].sync_id)) {
        item.isConflict = conflictDataRes[itemList[i].sync_id];
        if (item.isConflict) {
          conflictData[itemList[i].sync_id] = itemList[i];
          conflictData[itemList[i].name] = itemList[i];
        }
      }
      conflictQueue.push(item)
    }

    return { count, conflictData, conflictFileName, conflictQueue, conflictDataRes }
  }

  private getNewFileItem = async (item: IFile, newFileName: string, sharedata?: DirtyOutKeyItemShareKey[]): Promise<IShareKeyData> => {

    if (!sharedata) {
      sharedata = await this.DirtyOut.getShareKeyDataForSyncId(item.pid);
    }
    const share_names = await this.DirtyOut.getKeyArrayForSyncId(sharedata, item.pid, newFileName);

    const new_name = await this.SyncCrypt.filenameEncrypt(newFileName);
    return {
      share_names,
      new_name,
      sharedata
    };

  }

  private getPathList = async (sync_id: number) => {
    const data = await this.fileListService.getDataNoState(sync_id, '0', []);
    return data['pathlist'];
  }

  private getDuplicateFileNameAndGetNewFileName = async (filename: string, sync_id: number, selectedRows: IFile[],
    selectedDirPathList: IFile[]): Promise<string> => {
    if (!selectedDirPathList.length) {
      selectedDirPathList = await this.getPathList(sync_id);
    }
    const dotIndex = filename.lastIndexOf('.');
    const itemList: IFile[] = [...selectedDirPathList, ...selectedRows]
    let newFileName = filename;
    if (dotIndex == -1) {
      newFileName = filename + ' copy';
      while (itemList.find((item) => item.name === newFileName)) {
        newFileName += ' copy';
      }
      return newFileName;
    } else {
      newFileName = filename.substring(0, dotIndex);
      const extension: string = filename.substring(dotIndex);
      while (itemList.find((item) => item.name === newFileName + extension)) {
        newFileName += ' copy';
      }
      return newFileName + filename.substring(dotIndex);
    }
  }

  keepBoth = async (item: IFile, sync_id: number, selectedRows: IFile[], selectedDirPathList: IFile[], duplicateNameMap: IDuplicateNameMap = {}): Promise<IDuplicateNameMap> => {
    const conflictFileName: string = item.name;
    const newFileName = await this.getDuplicateFileNameAndGetNewFileName(conflictFileName, sync_id, selectedRows, selectedDirPathList)
    // const item: IFileConflict = { ...this.conflictData[conflictFileName] };
    duplicateNameMap[item.sync_id] = await this.getNewFileItem(item, newFileName);
    return duplicateNameMap;
  }

  getShareCheck = async (sync_id: number) => {

  }

  startMovingOrCopyExecution = async (destSyncId: number, itemList: IFile[], actionMoveOrCopy: WebpathActionType, duplicateNameMap: IDuplicateNameMap): Promise<any> => {
    if (!itemList.length) return false;
    const batchType: IBatchAction = actionMoveOrCopy == WebpathActionType.COPY ? this.batchCopyService : (this.isLinkPreserveAllowed ? this.atomicMoveService : this.batchMoveService);
    try {
      const sync_id_array: number[] = [];
      const sync_ids: string[] = [];
      const list = itemList;
      let files = 0;
      let directories = 0;

      for (let i = list.length - 1; i >= 0; i--) {
        sync_id_array.push(list[i].sync_id);
        sync_ids.push(`${list[i].sync_id}`)
        if (list[i].type && list[i].type.toUpperCase() === 'DIR') {
          directories++;
        } else {
          files++;
        }
      }
      await this.notificationsService.stopNotificationLoop();
      // set syncIds to be processed in the batch view
      // this way we'll be able to update the batch when an item is cancelled
      batchType.view.syncIds = sync_id_array;
      const result = await (<BatchMoveService | BatchCopyService>batchType).execute(
        sync_id_array,
        destSyncId,
        actionMoveOrCopy === WebpathActionType.MOVE,
        duplicateNameMap
      );
      console.warn('done transfer');
      switch (actionMoveOrCopy) {
        case 'Move':
          this.blendService.track(BlendEvent.MOVE_ITEMS, { num_items: list.length });
          break;
        case 'Copy':
          this.blendService.track(BlendEvent.COPY_ITEMS, { num_items: list.length });
          break;
      }
      return result;
    } catch (error) {
      console.error('transfer', error);
      return false;
    }
  }
}
